export function calculateAge(birthdateString, referenceDate = new Date()) {
  if (!birthdateString) return null;

  const [year, month, day] = birthdateString.split("-").map(Number);
  if (!year || !month || !day) return null;

  const birth = new Date(year, month - 1, day);
  let age = referenceDate.getFullYear() - birth.getFullYear();

  const m = referenceDate.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && referenceDate.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

function normalize(str) {
  if (!str) return "";
  return String(str).trim().toLowerCase();
}

// ✅ evita match falso por "Indefinido"
function isMeaningfulCity(cityNorm) {
  if (!cityNorm) return false;
  const bad = new Set([
    "indefinido",
    "indefinida",
    "nao informado",
    "não informado",
    "sem cidade",
    "n/a",
    "na",
    "undefined",
    "null",
  ]);
  return !bad.has(cityNorm);
}

function isMutualGenderPreference(userA, userB) {
  const genderA = normalize(userA.gender);
  const genderB = normalize(userB.gender);

  const searchA = normalize(userA.searchGender || "ambos");
  const searchB = normalize(userB.searchGender || "ambos");

  const aLikesB =
    searchA === "ambos" ||
    searchA === genderB ||
    (searchA === "homens" && genderB === "masculino") ||
    (searchA === "mulheres" && genderB === "feminino");

  const bLikesA =
    searchB === "ambos" ||
    searchB === genderA ||
    (searchB === "homens" && genderA === "masculino") ||
    (searchB === "mulheres" && genderA === "feminino");

  return aLikesB && bLikesA;
}

function buildAllPreferenceTags(user) {
  const tags = [];

  const pushArray = (arr) => {
    if (Array.isArray(arr)) {
      arr.forEach((t) => {
        const n = normalize(t);
        if (n) tags.push(n);
      });
    }
  };

  pushArray(user.lifestyleTags);
  pushArray(user.desiredLifestyle);
  pushArray(user.valuesImportant);
  pushArray(user.valuesEssential);
  pushArray(user.nonNegotiables);

  if (user.relationshipPurpose) {
    const p = normalize(user.relationshipPurpose);
    if (p) tags.push(`purpose:${p}`);
  }

  return tags;
}

export function calculateCompatibility(userA, userB, options = {}) {
  const {
    weightCity = 40,
    weightOtherCities = 20,
    weightAgeDiff0to5 = 15,
    weightAgeDiff6to10 = 8,
    weightMaxLifestyle = 30,
    weightPerLifestyleTag = 5,

    weightPurposeExact = 25,
    weightMaxValues = 25,
    weightPerValueTag = 4,
    weightMaxNonNegotiables = 15,
    weightPerNonNegotiableTag = 5,
  } = options;

  if (!userA || !userB) {
    return { isValid: false, score: 0, breakdown: {} };
  }

  if (userA.id === userB.id) {
    return { isValid: false, score: 0, breakdown: {} };
  }

  // ✅ FIX: se não tiver status, considera "ativo"
  const statusA = normalize(userA.status || "ativo");
  const statusB = normalize(userB.status || "ativo");
  if (statusA !== "ativo" || statusB !== "ativo") {
    return { isValid: false, score: 0, breakdown: {} };
  }

  const blockedA = Array.isArray(userA.blockedUsers) ? userA.blockedUsers : [];
  const blockedB = Array.isArray(userB.blockedUsers) ? userB.blockedUsers : [];

  if (blockedA.includes(userB.id) || blockedB.includes(userA.id)) {
    return { isValid: false, score: 0, breakdown: {} };
  }

  if (!isMutualGenderPreference(userA, userB)) {
    return {
      isValid: false,
      score: 0,
      breakdown: { reason: "Preferências de gênero incompatíveis" },
    };
  }

  let score = 0;
  const breakdown = {
    city: 0,
    age: 0,
    lifestyle: 0,
    values: 0,
    nonNegotiables: 0,
    purpose: 0,
  };

  // Cidade
  const cityA = normalize(userA.city);
  const cityB = normalize(userB.city);
  const acceptsOtherCitiesA = !!userA.acceptsOtherCities;
  const acceptsOtherCitiesB = !!userB.acceptsOtherCities;

  if (isMeaningfulCity(cityA) && isMeaningfulCity(cityB)) {
    if (cityA === cityB) {
      score += weightCity;
      breakdown.city = weightCity;
    } else if (acceptsOtherCitiesA || acceptsOtherCitiesB) {
      score += weightOtherCities;
      breakdown.city = weightOtherCities;
    }
  }

  // Idade
  const ageA = calculateAge(userA.birthdate);
  const ageB = calculateAge(userB.birthdate);

  if (ageA != null && ageB != null) {
    const diff = Math.abs(ageA - ageB);

    if (diff <= 5) {
      score += weightAgeDiff0to5;
      breakdown.age = weightAgeDiff0to5;
    } else if (diff <= 10) {
      score += weightAgeDiff6to10;
      breakdown.age = weightAgeDiff6to10;
    }
  }

  // Lifestyle
  const tagsA = (userA.lifestyleTags || []).map(normalize);
  const tagsB = (userB.lifestyleTags || []).map(normalize);

  if (tagsA.length && tagsB.length) {
    const setB = new Set(tagsB);
    let common = 0;

    tagsA.forEach((tag) => {
      if (setB.has(tag)) common++;
    });

    const lifestyleScore = Math.min(weightMaxLifestyle, common * weightPerLifestyleTag);

    score += lifestyleScore;
    breakdown.lifestyle = lifestyleScore;
  }

  // Purpose
  const purposeA = normalize(userA.relationshipPurpose);
  const purposeB = normalize(userB.relationshipPurpose);

  if (purposeA && purposeB && purposeA === purposeB) {
    score += weightPurposeExact;
    breakdown.purpose = weightPurposeExact;
  }

  // Values
  const valuesA = [
    ...(userA.valuesImportant || []),
    ...(userA.valuesEssential || []),
  ].map(normalize);

  const valuesB = [
    ...(userB.valuesImportant || []),
    ...(userB.valuesEssential || []),
  ].map(normalize);

  if (valuesA.length && valuesB.length) {
    const setB = new Set(valuesB);
    let common = 0;

    valuesA.forEach((v) => {
      if (setB.has(v)) common++;
    });

    const valuesScore = Math.min(weightMaxValues, common * weightPerValueTag);

    score += valuesScore;
    breakdown.values = valuesScore;
  }

  // Non-negotiables
  const nonNegA = (userA.nonNegotiables || []).map(normalize);
  const nonNegB = (userB.nonNegotiables || []).map(normalize);

  if (nonNegA.length && nonNegB.length) {
    const setB = new Set(nonNegB);
    let common = 0;

    nonNegA.forEach((n) => {
      if (setB.has(n)) common++;
    });

    const nnScore = Math.min(weightMaxNonNegotiables, common * weightPerNonNegotiableTag);

    score += nnScore;
    breakdown.nonNegotiables = nnScore;
  }

  // bônus geral
  const allTagsA = buildAllPreferenceTags(userA);
  const allTagsB = buildAllPreferenceTags(userB);

  if (allTagsA.length && allTagsB.length) {
    const setB = new Set(allTagsB);
    let extraCommon = 0;

    allTagsA.forEach((t) => {
      if (setB.has(t)) extraCommon++;
    });

    const bonus = Math.min(10, extraCommon * 1);
    score += bonus;
  }

  if (score < 0) score = 0;

  return {
    isValid: true,
    score,
    breakdown,
  };
}

export function generateSuggestionsForUser(user, allUsers, options = {}) {
  const {
    maxSuggestionsPerUser = 3,
    minScore = 40,
    now = new Date(),
  } = options;

  const suggestions = [];

  for (const other of allUsers) {
    const { isValid, score, breakdown } = calculateCompatibility(user, other, options);

    if (!isValid) continue;
    if (score < minScore) continue;

    suggestions.push({
      id: `sug_${user.id}_${other.id}`,
      userA: user.id,
      userB: other.id,
      score,
      breakdown,
      city:
        normalize(user.city) === normalize(other.city)
          ? user.city
          : `${user.city || ""}${user.city && other.city ? " / " : ""}${other.city || ""}`,
      origin: "ia",
      status: "suggested",
      createdAt: now.toISOString(),
    });
  }

  suggestions.sort((a, b) => b.score - a.score);
  return suggestions.slice(0, maxSuggestionsPerUser);
}

export function generateWeeklySuggestions(allUsers, options = {}) {
  const pairSet = new Set();
  const finalSuggestions = [];

  for (const user of allUsers) {
    const userSug = generateSuggestionsForUser(user, allUsers, options);

    for (const sug of userSug) {
      const key = [sug.userA, sug.userB].sort().join("|");
      if (pairSet.has(key)) continue;
      pairSet.add(key);

      finalSuggestions.push(sug);
    }
  }

  return finalSuggestions;
}

export function toFirestoreMatchSuggestionDoc(s) {
  return {
    userA: s.userA,
    userB: s.userB,
    score: s.score,
    breakdown: s.breakdown || null,
    city: s.city ?? null,
    origin: s.origin ?? "ia",
    status: s.status ?? "suggested",
    createdAt: s.createdAt ? new Date(s.createdAt) : new Date(),
  };
}
